import React, {
  ReactElement, useMemo, useRef, useState,
} from 'react';
import classNames from 'classnames';
import { IconColorPro } from '@sololearnorg/sol';
import useStyles from 'isomorphic-style-loader/useStyles';

import { ExpandIcon } from '../../assets/icons/ExpandIcon';
import { ProgrammingLanguage, i18n } from '../../../../symphony';
import { EditorLabels } from '../EditorLabels/EditorLabels';
import { ICodeSolution } from '../../../../api/public/learn-engine/learn-engine-api';
import { CodeLanguages } from '../../../../api/public/global';
import { ProgrammingLanguageToCodeLanguages } from '../../constants/constants';
import { SL_CODE_SUPPORTED_THEMES } from '../../../../code/public/global/constants';
import { RunButton } from '../RunButton/RunButton';

import s from './WidgetPlayground.scss';
import { ResetButton } from '../ResetButton/ResetButton';

interface WidgetPlaygroundProps {
  aiSolutionComponent?: ReactElement;
  shouldShowProLabel: boolean;
  onExpandClick: () => void;
  initialCodes: ICodeSolution[];
  onFocus?: () => void;
  onEditClick: (idx: number) => void;
  minimizedRefs: unknown;
  onChange: ({ value, lng }) => void;
  codes: string[];
  onResetClick: () => void;
  isResetButtonActive: boolean;
  onRunClick: () => void;
  defaultCodeLanguage: ProgrammingLanguage;
}

export const WidgetPlayground = ({
  aiSolutionComponent,
  shouldShowProLabel,
  onExpandClick,
  initialCodes,
  onFocus,
  minimizedRefs,
  onEditClick,
  onChange,
  codes,
  onResetClick,
  isResetButtonActive,
  onRunClick,
  defaultCodeLanguage
}: WidgetPlaygroundProps) => {
  useStyles(s);

  const [activeTab, setActiveTab] = useState<ProgrammingLanguage>(defaultCodeLanguage || initialCodes[0].languageId);

  const [showClickToEdit, setShowClickToEdit] = useState<boolean>(true);

  const [highestTab, setHighestTab] = useState<{
    lng: CodeLanguages,
    height: number;
  }>({ lng: null, height: 0 });

  const tabsContentHeight = useRef<{
    [key: string]: number;
  }>({}).current;

  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
    setShowClickToEdit(false);
  };

  const handleContentHeightChange = (lng: CodeLanguages, height: number) => {
    tabsContentHeight[lng] = height;

    let highestNumber = -Infinity;
    let highestKey = null;

    for (const key in tabsContentHeight) {
      const value = tabsContentHeight[key];
      if (value > highestNumber) {
        highestNumber = value;
        highestKey = key;
      }
    }

    setHighestTab({
      height: highestNumber,
      lng: highestKey,
    });
  };

  const handleEditClick = (idx: number) => {
    setShowClickToEdit(false);
    onEditClick(idx);
  };

  const isNode = typeof window === 'undefined';
  const { SlCodeEditor } = useMemo(
    // eslint-disable-next-line global-require
    () => (!isNode ? require('../../../../code/public/SlCodeEditor/SlCodeEditor') : {}),
    [],
  );

  return (
    <div className="sl-widget-playground">
      <div className="sl-widget-playground__header">
        <ExpandIcon className="sl-widget-playground__header__expand-icon" onClick={onExpandClick} />
        <div className="sl-widget-playground__right-side">
          {shouldShowProLabel && <IconColorPro />}
          {aiSolutionComponent && aiSolutionComponent}
        </div>
      </div>
      <EditorLabels isDarkMode languages={initialCodes.map((c) => c.languageId)} activeTab={activeTab} onTabNameClick={(lng) => setActiveTab(lng)} />
      {initialCodes.map((c, idx) => {
        return (
          <div
            key={c.languageId}
            className={classNames('sl-widget-playground__tab', {
              active: activeTab === c.languageId,
            })}
          >
            <SlCodeEditor
              ref={minimizedRefs && minimizedRefs[idx]}
              width="100%"
              key={c.languageId}
              highestTab={highestTab}
              isTIYWidget
              onFocus={handleFocus}
              onBlur={() => setShowClickToEdit(true)}
              paddingBeforeFirstLine={1}
              onChange={onChange}
              onContentHeightChange={handleContentHeightChange}
              value={codes[idx]}
              language={ProgrammingLanguageToCodeLanguages[c.languageId]}
              theme={SL_CODE_SUPPORTED_THEMES.DARK}
            />
            {showClickToEdit && (
              <button
                type="button"
                onClick={() => handleEditClick(idx)}
                className="sl-widget-playground__edit-btn"
              >
                {i18n.t('code_playground.try-it-yourself.click-to-edit')}
              </button>
            )}
          </div>);
      })}
      <div className="sl-widget-playground__footer">
        <ResetButton
          onClick={onResetClick}
          disabled={!isResetButtonActive}
        />
        <RunButton onClick={onRunClick} />
      </div>
    </div>
  );
};
